import { Suspense } from "react";
import { useSelector } from "react-redux";
import { Outlet } from "react-router";
import { ImpersonationBanner } from "@/shared/components/ImpersonationBanner";
import Loading from "@/shared/components/Loading";
import { useOrganizationPlan } from "@/shared/hooks/useOrganizationFeatures";
import { Button } from "@/shared/ui/Button";
import type { RootState } from "@/store";

// Full-viewport page opened in its own tab, without the console sidebar. Waits for
// the plan so FeatureProtectedRoute does not flash an error page on a fresh tab.
// On a failed plan request the outlet stays unmounted: the guard would refetch the
// plan on mount, toggle this layout back to loading and loop on the failing endpoint.
const StandaloneLayout = () => {
  const { isLoading: isPlanLoading, refetch } = useOrganizationPlan();
  const hasPlanError = useSelector((state: RootState) => state.session.error?.code === "plan_fetch_error");

  const renderContent = () => {
    if (isPlanLoading) {
      return (
        <div className="flex flex-1 items-center justify-center">
          <Loading state={{ loading: true }} role="status" aria-label="Chargement" />
        </div>
      );
    }

    if (hasPlanError) {
      return (
        <div className="flex flex-1 flex-col items-center justify-center gap-4 p-6 text-center">
          <p className="text-muted-foreground">
            Impossible de charger votre espace. Vérifiez votre connexion puis réessayez.
          </p>
          <Button variant="outline" onClick={refetch}>Réessayer</Button>
        </div>
      );
    }

    return (
      <Suspense fallback={null}>
        <Outlet />
      </Suspense>
    );
  };

  return (
    <main className="flex h-svh flex-col overflow-hidden">
      <ImpersonationBanner />
      <div className="flex min-h-0 flex-1 flex-col">
        {renderContent()}
      </div>
    </main>
  );
};

export default StandaloneLayout;
